import type { HttpError } from '@refinedev/core'
import { useList } from '@refinedev/core'
import type { GameConfiguration } from '@sirius/contracts/admin'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { USE_DEFAULT } from '~/lib/inheritable'
import { configurationName } from '~/pages/settings/defaultConfigurations'
import { TENANT_CONFIGURATIONS_RESOURCE } from '~/providers/dataProvider'
import { configurationOptionLabel } from './configurations'

/**
 * The configuration one of a cabinet's games plays, "Use default (<name>)"
 * (follows the tenant's default configuration of the game, else the game's)
 * or one of its configurations the host tenant may run, the platform's and
 * its own, by name ("Name · saved <date>", the tenant's own marked "own").
 * Only published configurations are offered: the cabinet boots the
 * configuration's current published version, so it picks up every publish
 * of it at its next start with no round open (never a draft). For staff and the
 * host tenant's owners/admins (the cabinet form's `cabinets` `configure`
 * access): `games.configurations.list` answers FORBIDDEN to members, so they
 * never render it.
 */
export function ConfigurationSelect({
  gameId,
  gameName,
  value,
  valueLabel,
  inheritedConfigurationId,
  disabled,
  onChange,
}: {
  gameId: string
  gameName: string
  value: string
  /**
   * what to call `value` while the configurations load (`Low`, from the
   * cabinet): without an item for it the select would show nothing
   */
  valueLabel: string | undefined
  /**
   * what Use default resolves to (`inheritedConfigurationId`); undefined
   * while it loads, null for none
   */
  inheritedConfigurationId: string | null | undefined
  disabled: boolean
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const { result, query } = useList<GameConfiguration, HttpError>({
    resource: TENANT_CONFIGURATIONS_RESOURCE,
    filters: [{ field: 'gameId', operator: 'eq', value: gameId }],
    sorters: [{ field: 'name', order: 'asc' }],
    pagination: { mode: 'off' },
  })
  // published ones only: the backend's `availableTo` leaves out the others,
  // and a cabinet cannot run a configuration never published
  const configurations = result.data.filter(
    (configuration) => configuration.currentConfigId !== null,
  )
  const inheritedName = configurationName(
    query.isSuccess ? configurations : undefined,
    inheritedConfigurationId,
  )
  const listed =
    value === USE_DEFAULT ||
    configurations.some((configuration) => configuration.id === value)

  return (
    <div className="grid gap-1">
      <Select
        value={value}
        disabled={disabled}
        // Radix reports '' while the value has no item yet (configurations
        // still loading); that would wipe the cabinet's configuration
        onValueChange={(next) => {
          if (next !== '') {
            onChange(next)
          }
        }}
      >
        <SelectTrigger
          aria-label={t('cabinetsPage.form.configurationAria', {
            game: gameName,
          })}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={USE_DEFAULT}>
            {inheritedName === undefined || inheritedName === null
              ? t('shell.common.useDefault')
              : t('shell.common.useDefaultWith', {
                  name: inheritedName,
                })}
          </SelectItem>
          {!listed && (
            <SelectItem
              value={value}
              disabled
            >
              {valueLabel ?? t('cabinetsPage.form.assignedConfiguration')}
            </SelectItem>
          )}
          {configurations.map((configuration) => (
            <SelectItem
              key={configuration.id}
              value={configuration.id}
            >
              {configurationOptionLabel(configuration, t)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {query.isError && (
        <p className="text-destructive text-sm">
          {t('cabinetsPage.form.configurationsLoadError', { game: gameName })}
        </p>
      )}
    </div>
  )
}
